import React, { useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../firebase/firebaseConfig';
import './AddEventPage.css'; // reuse shared form styles

const DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
];

const INITIAL = {
  title:    '',
  body:     '',
  ctaText:  'Learn More',
  ctaUrl:   '',
  target:   'all',
};

export default function SendPromoPopupPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // Progressive disclosure - each optional section is hidden until
  // the admin clicks the matching "Add" button.
  const [showHeadline, setShowHeadline] = useState(false);
  const [showBody,     setShowBody]     = useState(false);
  const [showButton,   setShowButton]   = useState(false);

  const addHeadline   = () => setShowHeadline(true);
  const addBody       = () => setShowBody(true);
  const addButton     = () => setShowButton(true);

  const removeHeadline = () => {
    setShowHeadline(false);
    setForm(p => ({ ...p, title: '' }));
  };
  const removeBody = () => {
    setShowBody(false);
    setForm(p => ({ ...p, body: '' }));
  };
  const removeButton = () => {
    setShowButton(false);
    setForm(p => ({ ...p, ctaText: '', ctaUrl: '' }));
  };

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '❌ Please select an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setToast({ type: 'error', message: '❌ Image must be under 5 MB.' });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }, []);

  const clearImage = useCallback(() => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!imageFile) e.image = 'An image is required.';
    if (showHeadline && !form.title.trim())
      e.title = 'Headline is required (or remove this section).';
    if (form.title.trim().length > 60)
      e.title = 'Keep the headline under 60 characters.';
    if (showButton) {
      if (!form.ctaText.trim()) e.ctaText = 'Button text is required.';
      if (!form.ctaUrl.trim())  e.ctaUrl  = 'Button URL is required.';
      if (form.ctaUrl.trim()) {
        try { new URL(form.ctaUrl.trim()); }
        catch { e.ctaUrl = 'Must be a valid URL (https://…).'; }
      }
    }
    return e;
  }, [form, imageFile, showHeadline, showButton]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (!window.confirm(
      `Send this promo popup to ${form.target === 'all'
        ? 'ALL members'
        : `members in ${form.target}`}?`
    )) return;

    setSubmitting(true);
    setToast(null);

    try {
      // Upload the chosen image first, then pass the resulting
      // download URL to the Cloud Function.
      let imageUrl;
      if (imageFile) {
        const ext = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `promo_images/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, imageFile, {
          contentType: imageFile.type,
        });
        imageUrl = await getDownloadURL(storageRef);
      }

      const call = httpsCallable(functions, 'sendNotification');
      await call({
        type:     'promo',
        title:    form.title.trim() || undefined,
        body:     form.body.trim()  || undefined,
        imageUrl,
        ctaText:  form.ctaText.trim() || undefined,
        ctaUrl:   form.ctaUrl.trim()  || undefined,
        target:   form.target,
      });

      setToast({
        type: 'success',
        message: `✅ Promo popup sent to ${
          form.target === 'all' ? 'all members' : form.target
        }.`,
      });
      setForm(INITIAL);
      clearImage();
      setShowHeadline(false);
      setShowBody(false);
      setShowButton(false);
    } catch (err) {
      setToast({
        type: 'error',
        message: `❌ Failed: ${err.message || err.code || 'Unknown error'}`,
      });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Send Promo Popup</h1>
          <p className="page-subtitle">
            Rich in-app modal with image + tappable button.
            Use for big announcements, sales, sign-up drives.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`amf-toast amf-toast--${toast.type}`}
          style={{ marginBottom: 24 }}
        >
          {toast.message}
          <button
            className="amf-toast-close"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          {/* ── Left column: image first, then progressive sections ── */}
          <div className="aep-col">
            <div className="amf-field">
              <label className="amf-label">
                Promo Image <span className="amf-required">*</span>
              </label>
              {errors.image && (
                <p className="amf-error-text" style={{ marginBottom: 8 }}>
                  {errors.image}
                </p>
              )}

              {imagePreview ? (
                <div style={{
                  position: 'relative',
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid rgba(212, 181, 116, 0.25)',
                }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: 220,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    style={{
                      position: 'absolute',
                      top: 8, right: 8,
                      padding: '6px 12px',
                      background: 'rgba(0,0,0,0.7)',
                      border: '1px solid #E63946',
                      borderRadius: 6,
                      color: '#E63946',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                  <div style={{
                    padding: '8px 12px',
                    fontSize: 11,
                    color: '#B79F6D',
                    background: '#0F0F0F',
                    fontFamily: 'monospace',
                  }}>
                    {imageFile?.name} · {imageFile && (imageFile.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '32px 16px',
                    background: '#161618',
                    border: '2px dashed rgba(212, 181, 116, 0.35)',
                    borderRadius: 10,
                    color: '#D8C8A4',
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 30 }}>🖼️</span>
                  <span style={{ fontWeight: 700 }}>Click to upload image</span>
                  <span style={{ color: '#B79F6D', fontSize: 11, textAlign: 'center', lineHeight: 1.4 }}>
                    JPG, PNG, or WebP · max 5 MB
                    <br />
                    Recommended: <strong>16:9 aspect, ~800×450 px</strong>
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </div>

            {/* ─── Progressive sections ─── */}

            {/* Headline */}
            {showHeadline ? (
              <OptionalSection
                title="Headline"
                onRemove={removeHeadline}
              >
                <Field label="Headline" required error={errors.title}>
                  <input
                    name="title"
                    className={`amf-input ${errors.title ? 'amf-input--error' : ''}`}
                    placeholder="e.g. Annual Service Discount — 30% Off"
                    maxLength={70}
                    value={form.title}
                    onChange={handleChange}
                  />
                </Field>
              </OptionalSection>
            ) : (
              <AddPill label="+ Add Headline" onClick={addHeadline} />
            )}

            {/* Body */}
            {showBody ? (
              <OptionalSection
                title="Body"
                onRemove={removeBody}
              >
                <Field label="Body">
                  <textarea
                    name="body"
                    className="amf-input aep-textarea"
                    placeholder="Tell members what this is about and what they get…"
                    rows={4}
                    value={form.body}
                    onChange={handleChange}
                  />
                </Field>
              </OptionalSection>
            ) : (
              <AddPill label="+ Add Body" onClick={addBody} />
            )}

            {/* Button */}
            {showButton ? (
              <OptionalSection
                title="Button"
                onRemove={removeButton}
              >
                <Field label="Button text" required error={errors.ctaText}>
                  <input
                    name="ctaText"
                    className={`amf-input ${errors.ctaText ? 'amf-input--error' : ''}`}
                    placeholder="e.g. Learn More, Book Service, Buy Now"
                    maxLength={28}
                    value={form.ctaText}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Button link URL" required error={errors.ctaUrl}>
                  <input
                    name="ctaUrl"
                    type="url"
                    className={`amf-input ${errors.ctaUrl ? 'amf-input--error' : ''}`}
                    placeholder="https://teamfronx.com/promo"
                    value={form.ctaUrl}
                    onChange={handleChange}
                  />
                  <p className="amf-hint">
                    Opens in browser when the user taps the button.
                  </p>
                </Field>
              </OptionalSection>
            ) : (
              <AddPill label="+ Add Button" onClick={addButton} />
            )}
          </div>

          {/* ── Right column: target + preview ────────── */}
          <div className="aep-col">
            <Field label="Send To" required>
              <select
                name="target"
                className="amf-select"
                value={form.target}
                onChange={handleChange}
              >
                <option value="all">🌐 All Members</option>
                <optgroup label="By District">
                  {DISTRICTS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </optgroup>
              </select>
            </Field>

            <Field label="Live Preview">
              <div
                style={{
                  background: 'linear-gradient(180deg, #1A1A1A, #0F0F0F)',
                  border: '1.5px solid rgba(212, 181, 116, 0.35)',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.55), 0 0 40px rgba(212,181,116,0.12)',
                }}
              >
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt=""
                    style={{
                      width: '100%',
                      height: 140,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                )}
                <div style={{ padding: 18 }}>
                  <div style={{
                    color: '#D4B574',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1,
                    marginBottom: 10,
                  }}>
                    Team Fronx
                  </div>
                  <div style={{
                    color: '#F7E8BF',
                    fontSize: 18,
                    fontWeight: 900,
                    lineHeight: 1.2,
                    marginBottom: 6,
                  }}>
                    {form.title.trim() || 'Your headline appears here'}
                  </div>
                  <div style={{
                    color: '#D8C8A4',
                    fontSize: 13,
                    lineHeight: 1.5,
                    marginBottom: 16,
                  }}>
                    {form.body.trim() || 'Body text will show here…'}
                  </div>
                  <div style={{
                    background: '#D4B574',
                    color: '#000',
                    textAlign: 'center',
                    padding: '12px',
                    borderRadius: 10,
                    fontWeight: 800,
                    fontSize: 14,
                    letterSpacing: 0.5,
                  }}>
                    {form.ctaText.trim() || 'BUTTON'}
                  </div>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div className="aep-submit-row">
          <button
            type="submit"
            className="amf-submit-btn"
            disabled={submitting}
          >
            {submitting
              ? <><span className="amf-spinner" /> Sending…</>
              : 'Send Promo Popup'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}

function AddPill({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        marginTop: 12,
        marginBottom: 12,
        padding: '10px 16px',
        background: 'transparent',
        border: '1px dashed rgba(212, 181, 116, 0.45)',
        borderRadius: 10,
        color: '#D4B574',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.3,
        cursor: 'pointer',
        transition: 'background 120ms ease',
        width: '100%',
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(212, 181, 116, 0.07)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {label}
    </button>
  );
}

function OptionalSection({ title, onRemove, children }) {
  return (
    <div style={{
      marginTop: 12,
      marginBottom: 12,
      padding: '14px 14px 6px',
      background: 'rgba(212, 181, 116, 0.04)',
      border: '1px solid rgba(212, 181, 116, 0.22)',
      borderRadius: 10,
      position: 'relative',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
      }}>
        <span style={{
          color: '#D4B574',
          fontSize: 11,
          letterSpacing: 1.5,
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {title}
        </span>
        <button
          type="button"
          onClick={onRemove}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#B79F6D',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            padding: '4px 8px',
          }}
          title={`Remove ${title}`}
        >
          ×&nbsp;Remove
        </button>
      </div>
      {children}
    </div>
  );
}
